import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BlockType } from '../../../shared/types'

export interface EditorState {
  content: string
  isEditing: boolean
  isSaving: boolean
  lastSavedAt: number | null
  lastEditedAt: number | null
  autoSaveEnabled: boolean
  currentBlockId: string | null
  selection: {
    from: number
    to: number
  } | null
  selectedBlocks: string[]
  editorConfig: {
    fontSize: number
    lineHeight: number
    fontFamily: string
    theme: 'light' | 'dark'
  }
}

const initialState: EditorState = {
  content: '',
  isEditing: false,
  isSaving: false,
  lastSavedAt: null,
  lastEditedAt: null,
  autoSaveEnabled: true,
  currentBlockId: null,
  selection: null,
  selectedBlocks: [],
  editorConfig: {
    fontSize: 16,
    lineHeight: 1.6,
    fontFamily: 'Inter, sans-serif',
    theme: 'light'
  }
}

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setContent: (state, action: PayloadAction<string>) => {
      state.content = action.payload
    },

    setContentAndEditing: (state, action: PayloadAction<string>) => {
      state.content = action.payload
      state.isEditing = true
    },
    
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload
    },

    markEdited: (state) => {
      state.lastEditedAt = Date.now()
    },
    
    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload
    },
    
    setLastSavedAt: (state, action: PayloadAction<number>) => {
      state.lastSavedAt = action.payload
    },
    
    setAutoSaveEnabled: (state, action: PayloadAction<boolean>) => {
      state.autoSaveEnabled = action.payload
    },
    
    setCurrentBlockId: (state, action: PayloadAction<string | null>) => {
      state.currentBlockId = action.payload
    },
    
    setSelection: (state, action: PayloadAction<{ from: number; to: number } | null>) => {
      state.selection = action.payload
    },
    
    setSelectedBlocks: (state, action: PayloadAction<string[]>) => {
      state.selectedBlocks = action.payload
    },
    
    addSelectedBlock: (state, action: PayloadAction<string>) => {
      if (!state.selectedBlocks.includes(action.payload)) {
        state.selectedBlocks.push(action.payload)
      }
    },
    
    removeSelectedBlock: (state, action: PayloadAction<string>) => {
      state.selectedBlocks = state.selectedBlocks.filter(id => id !== action.payload)
    },
    
    clearSelectedBlocks: (state) => {
      state.selectedBlocks = []
    },
    
    setEditorConfig: (state, action: PayloadAction<Partial<EditorState['editorConfig']>>) => {
      state.editorConfig = { ...state.editorConfig, ...action.payload }
    },
    
    insertBlock: (state, action: PayloadAction<{ content: string; type: BlockType; position: number }>) => {
      // This would handle block insertion logic
      // For now, we'll just update the content
      const { content, position } = action.payload
      const before = state.content.slice(0, position)
      const after = state.content.slice(position)
      state.content = before + content + after
      state.isEditing = true
    },
    
    replaceSelection: (state, action: PayloadAction<string>) => {
      if (state.selection) {
        const { from, to } = state.selection
        const before = state.content.slice(0, from)
        const after = state.content.slice(to)
        state.content = before + action.payload + after
        state.isEditing = true
      }
    },
    
    clearEditor: (state) => {
      state.content = ''
      state.isEditing = false
      state.isSaving = false
      state.lastSavedAt = null
      state.lastEditedAt = null
      state.currentBlockId = null
      state.selection = null
    },
    
    resetEditor: () => initialState
  }
})

export const {
  setContent,
  setContentAndEditing,
  setEditing,
  markEdited,
  setSaving,
  setLastSavedAt,
  setAutoSaveEnabled,
  setCurrentBlockId,
  setSelection,
  setSelectedBlocks,
  addSelectedBlock,
  removeSelectedBlock,
  clearSelectedBlocks,
  setEditorConfig,
  insertBlock,
  replaceSelection,
  clearEditor,
  resetEditor
} = editorSlice.actions

export const updateEditorContent = setContentAndEditing

export default editorSlice.reducer